import React, { useState, useEffect } from 'react';
import { IconButton, Icon, Select, MenuItem, Alert } from '@mui/material';
import MuiCloseIcon from '@mui/icons-material/Close';
import {
  ButtonContainer,
  CloseIcon,
  Container,
  InputContainer,
  SelectWrap,
  ShareButton,
  ShareDialog,
  Subtitle,
  TitleRow,
} from './ShareModal.styles';
import { replace, first, get, last, uniq, flatten, size } from 'global/lib/lodash';
import { copyToClipboard, addNewLinesToText, checkCanShare, createAppUrl, stringObj } from 'global/utils';
import SnackAlerts from 'global/components/snack-alerts';
import { getTradeName } from '../../../global/components/trade-select';
import { CenterLoader } from '../../../global/styles';
import sendData from 'redux/actions/sendData';

const mediaUrl = (media) => get(media, 'full7DayUrl') || get(media, 'fullUrl');

const collectTradeOptions = (items, getTrades) => {
  const tradeOptions = [];
  let total = 0;
  (items || []).forEach((item) => {
    total++;
    (getTrades(item) || []).forEach((trade) => {
      const existing = tradeOptions.find((t) => t.id == trade.id);
      if (existing) existing.total++;
      else tradeOptions.push({ ...trade, total: 1 });
    });
  });
  return { tradeOptions, total };
};

export default (props) => {
  const [error, setError] = useState();
  const [alerts, setAlerts] = useState();
  const [canShare, setCanShare] = useState(false);
  const [selectOpts, setSelectOpts] = useState([]);
  const [selected, setSelected] = useState();
  const [state, setState] = useState({ loading: false, id: undefined, urls: {} });

  const title = props.title || 'Share PDF Link';
  const showTradeSelect = size(selectOpts) > 0;

  const handleClose = () => props.onClose();

  const getLink = (lnk = selected) => {
    const key = lnk == null ? 'all' : lnk;
    return state.urls[key] || (key == 'all' ? props.link : undefined);
  };

  const copyLink = () => {
    setAlerts();
    copyToClipboard(getLink());
    setAlerts({ message: 'Link copied to clipboard.' });
  };

  const openLink = () => window.open(getLink(), '_blank');

  const copyToEmail = () => {
    const repl = { '\\?': '%3F', '\\&': '%26' };
    const body =
      props.emailText ||
      (props.isScorePdf
        ? `Assessment scores from ${props.assessmentName || 'this assessment'} have been shared with you. Please review the PDF here: 

${getLink()}

See the entire Assessment here:

${createAppUrl('')}

Thanks,
`
        : `You've been assigned a new task, please review the pdf here: ${getLink()}

Thanks,
`);

    let txt = addNewLinesToText(body, '%0D%0A%0D%0A');
    Object.keys(repl).forEach((k) => {
      txt = replace(txt, new RegExp(k, 'g'), repl[k]);
    });
    const subject = props.isScorePdf ? 'Assessment Score PDF' : title;
    const emails = uniq(flatten((props.tasks || []).map((t) => get(t, 'assignees', []).map((a) => a.email).filter(Boolean)))).join(',');
    window.open(`mailto:${emails || ''}?subject=${subject}&body=${txt}`, '_blank');
  };

  const share = async () => {
    try {
      await navigator.share({ title, url: getLink() });
    } catch (err) {
      console.warn(err);
    }
  };

  const createPdf = async (val) => {
    const messageId = state.id || props.messageId;
    const tradeOpt = selectOpts.find((o) => o.id == val);
    const tradeAttrs = {
      trade_type: val === 'all' ? 'all' : get(tradeOpt, 'name'),
      trade_type_id: val === 'all' ? null : val,
    };
    const tradeRels = {
      tradeTypes: {
        data: val === 'all' ? [] : [{ id: val, type: 'trade-types' }],
      },
    };

    if (props.isScorePdf) {
      const scorecard_score_ids = [];
      const sub_score_ids = [];
      (props.scoreItems || []).forEach((cp) => {
        if (cp.isSubScore) sub_score_ids.push(cp.id);
        else if (get(cp, 'scorecardScoreId')) scorecard_score_ids.push(`${get(cp, 'scorecardScoreId')}`);
      });

      const resp = await sendData(
        {
          url: `/messages${messageId ? `/${messageId}` : ''}`,
          method: messageId ? 'patch' : 'post',
          data: {
            data: {
              type: 'messages',
              id: messageId,
              attributes: stringObj({
                message_type: 'assessment_scores',
                model_type: 'assessment',
                model_id: props.assessmentId,
                realm_id: props.realmId,
                status: 1,
                body: 'Test',
                ...(val !== 'all' ? tradeAttrs : {}),
              }),
              relationships: {
                scorecardScores: {
                  data: scorecard_score_ids.map((id) => ({ type: 'scorecard-scores', id: id.toString() })),
                },
                subScores: size(sub_score_ids)
                  ? { data: sub_score_ids.map((id) => ({ type: 'sub-scores', id: id.toString() })) }
                  : undefined,
                ...(val !== 'all' ? tradeRels : {}),
              },
            },
          },
        },
        { normalize: { build: ['messages'] }, params: { include: 'media' } }
      );
      return last(resp);
    }

    const resp = await sendData(
      {
        url: `/messages${messageId ? `/${messageId}` : ''}`,
        method: messageId ? 'patch' : 'post',
        data: {
          data: {
            type: 'messages',
            id: messageId,
            attributes: {
              title: 'New Task',
              body: 'Test',
              message_type: 'task_share',
              model_type: 'task',
              realm_id: get(last(props.tasks), 'realm.id'),
              status: 1,
              ...tradeAttrs,
            },
            relationships: {
              tasks: { data: (props.tasks || []).map((t) => ({ id: t.id, type: 'tasks' })) },
              ...tradeRels,
            },
          },
        },
      },
      { normalize: { build: ['messages'] }, params: { include: 'media' } }
    );
    return last(resp);
  };

  const changeSelected = async (e) => {
    try {
      const val = e.target.value;
      setSelected(val);
      if (getLink(val)) return;

      setState((s) => ({ ...s, loading: true }));
      const res = await createPdf(val);
      const url = mediaUrl(last(get(res, 'media', []))) || mediaUrl(first(get(res, 'media', [])));
      if (!url) throw new Error('Could not generate the PDF for the selected items.');

      setState((s) => ({
        ...s,
        id: res.id,
        urls: { ...s.urls, [val]: url },
      }));
    } catch (err) {
      setAlerts({ message: err.toString(), severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (!props.open) {
      setState({ loading: false, id: undefined, urls: {} });
      setSelectOpts([]);
      setSelected();
      return;
    }

    setCanShare(checkCanShare());

    if (props.isScorePdf) {
      const { tradeOptions, total } = collectTradeOptions(props.scoreItems, (item) =>
        item.isSubScore ? item.tradeTypes : []
      );
      // Hide trade menu when only scores are selected (no trade types).
      setSelectOpts(size(tradeOptions) ? [{ name: 'All Trades', id: 'all', total }, ...tradeOptions] : []);
      setSelected('all');
      setState((s) => ({
        ...s,
        id: props.messageId,
        urls: props.link ? { all: props.link } : s.urls,
      }));
      if (!props.link) changeSelected({ target: { value: 'all' } });
    } else if (!props.link) {
      const { tradeOptions, total } = collectTradeOptions(props.tasks, (t) => t.tradeTypes);
      const allOpts = [{ name: 'All Trades', id: 'all', total }, ...tradeOptions];
      setSelectOpts(allOpts);
      changeSelected({ target: { value: first(allOpts).id } });
    }

    setTimeout(() => {
      try {
        if (checkCanShare()) document.getElementById('ShareBtn').click();
      } catch (err) {
        console.warn(err);
      }
    }, 500);
  }, [props.open]);

  return (
    <ShareDialog disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <Container>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setError()}>
                <MuiCloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <CloseIcon onClick={handleClose} size="large">
          <Icon>close</Icon>
        </CloseIcon>
        <TitleRow>
          <Subtitle variant="h5">{title}</Subtitle>
        </TitleRow>

        <InputContainer>
          <ButtonContainer>
            {!!state.loading && <CenterLoader size={60} />}
            {!state.loading && (
              <>
                {showTradeSelect && (
                  <SelectWrap>
                    <Select size="small" fullWidth variant="outlined" value={selected} onChange={changeSelected}>
                      {selectOpts.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{`${getTradeName(s)} (${s.total})`}</MenuItem>
                      ))}
                    </Select>
                  </SelectWrap>
                )}

                <ShareButton onClick={openLink} variant="outlined" color="secondary">
                  VIEW PDF
                </ShareButton>
                <ShareButton onClick={copyLink} startIcon={<Icon>filter_none</Icon>} variant="contained" color="secondary">
                  COPY LINK
                </ShareButton>
                <ShareButton onClick={copyToEmail} startIcon={<Icon>mail</Icon>} variant="contained" color="secondary">
                  COPY TO EMAIL
                </ShareButton>
                {canShare && (
                  <ShareButton onClick={share} startIcon={<Icon>share</Icon>} variant="contained" color="secondary" id="ShareBtn">
                    SHARE
                  </ShareButton>
                )}
              </>
            )}
          </ButtonContainer>
        </InputContainer>
      </Container>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </ShareDialog>
  );
};
